import { LoggerService } from "@Front/Services/LoggerService/LoggerService";
import { CustomerStore } from "@Front/Stores/CustomerStore";
import { ThirdPartyStore } from "@Front/Stores/ThirdPartyStore";
import { UserStore } from "@Front/Stores/UserStore";
import {
	getLeCoffreCustomerAccessTokenCookie,
	getLeCoffreNotaryAccessTokenCookie,
	getLeCoffreThirdPartyAccessTokenCookie,
	type LeCoffreAuthContext,
} from "@Front/Utils/lecoffreSessionCookieHelpers";
import { resolveRouteContextFamily } from "@Front/Utils/routeContextRegistry";
import type { SessionPresenceSnapshot } from "./bootstrapSessionContextIsolationTypes";
import { clearSessionFamilyWhenNoPresence, persistActiveSessionFamilyForRoute } from "./bootstrapSessionContextIsolationHelpers";

function readSessionPresenceSnapshot(): SessionPresenceSnapshot {
	return {
		hasNotaryStore: UserStore.instance.isConnected(),
		hasCustomerStore: CustomerStore.instance.isConnected(),
		hasThirdPartyStore: ThirdPartyStore.instance.isConnected(),
		hasNotaryCookie: Boolean(getLeCoffreNotaryAccessTokenCookie()),
		hasCustomerCookie: Boolean(getLeCoffreCustomerAccessTokenCookie()),
		hasThirdPartyCookie: Boolean(getLeCoffreThirdPartyAccessTokenCookie()),
	};
}

async function clearNotarySessionIfPresent(snapshot: SessionPresenceSnapshot): Promise<boolean> {
	if (!snapshot.hasNotaryStore && !snapshot.hasNotaryCookie) {
		return false;
	}
	await UserStore.instance.disconnect();
	return true;
}

async function clearClientSessionsIfPresent(snapshot: SessionPresenceSnapshot): Promise<boolean> {
	const shouldClearCustomer = snapshot.hasCustomerStore || snapshot.hasCustomerCookie;
	const shouldClearThirdParty = snapshot.hasThirdPartyStore || snapshot.hasThirdPartyCookie;
	if (!shouldClearCustomer && !shouldClearThirdParty) {
		return false;
	}
	if (shouldClearCustomer) {
		await CustomerStore.instance.disconnect();
	}
	if (shouldClearThirdParty) {
		await ThirdPartyStore.instance.disconnect();
	}
	return true;
}

/**
 * Purge client/third-party stores and cookies before establishing a notary session (e.g. IdNot login).
 * Prevents customer JWT from overriding notary permission context on neutral routes (/login, callback).
 */
export async function purgeClientAndThirdPartySessionsBeforeNotaryConnect(): Promise<boolean> {
	const before = readSessionPresenceSnapshot();
	const cleared = await clearClientSessionsIfPresent(before);
	if (cleared) {
		LoggerService.info("ℹ️ [SessionContext] Cleared client/third-party sessions before notary connect", { before });
	}
	return cleared;
}

async function applyRouteSessionIsolation(
	routeFamily: "client" | "notary",
	before: SessionPresenceSnapshot,
): Promise<boolean> {
	if (routeFamily === "client") {
		return clearNotarySessionIfPresent(before);
	}
	return clearClientSessionsIfPresent(before);
}

export async function isolateSessionContextForRoute(
	pathname: string,
	trigger: "bootstrap" | "navigation" = "bootstrap",
): Promise<boolean> {
	const routeFamily = resolveRouteContextFamily(pathname);
	if (routeFamily === "neutral") {
		return false;
	}
	const before = readSessionPresenceSnapshot();
	const isolationApplied = await applyRouteSessionIsolation(routeFamily, before);
	const after = readSessionPresenceSnapshot();
	persistActiveSessionFamilyForRoute(routeFamily, after);
	clearSessionFamilyWhenNoPresence(after);
	LoggerService.info("ℹ️ [SessionContextBootstrap] Effective context source", {
		trigger,
		routeFamily,
		pathname,
		isolationApplied,
		before,
		after,
	});
	return isolationApplied;
}

/** @deprecated Use isolateSessionContextForRoute — kept for call sites. */
export async function isolateSessionContextOnBootstrap(pathname: string): Promise<void> {
	await isolateSessionContextForRoute(pathname, "bootstrap");
}

export function resolveActiveAuthContextForRouteFamily(
	routeFamily: ReturnType<typeof resolveRouteContextFamily>,
): LeCoffreAuthContext | null {
	if (routeFamily === "notary") {
		if (UserStore.instance.isConnected() || getLeCoffreNotaryAccessTokenCookie()) {
			return "notary";
		}
		return null;
	}
	if (routeFamily === "client") {
		if (CustomerStore.instance.isConnected() || getLeCoffreCustomerAccessTokenCookie()) {
			return "customer";
		}
		if (ThirdPartyStore.instance.isConnected() || getLeCoffreThirdPartyAccessTokenCookie()) {
			return "thirdparty";
		}
		return null;
	}
	return null;
}
